import SelectField from '@/components/selectField';
import TextField from '@/components/textField';
import { useApiContext } from '@/contexts/apiContext';
import { FormHelperText, FormLabel, Grid } from '@mui/material';
import { useEffect, useState } from 'react';

type Props = {
  label?: string;
  required?: boolean;
  ftPrompts: any[];
  error?: string;
  rows?: number;
  onChange: (value: any) => void;
};

const FileTypePrompts = ({ label, required = false, ftPrompts = [], error, rows = 4, onChange }: Props) => {
  const apiContext = useApiContext();

  const [fileTypes, setFileTypes] = useState<any[]>([]);
  const [fileTypeOptions, setFileTypeOptions] = useState<any[]>([]);
  const [fileType, setFileType] = useState<any>();

  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPrompt, setUserPrompt] = useState('');

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<any>();

  useEffect(() => {
    const getFileTypes = async () => {
      try {
        setLoading(true);
        const fileTypes = await apiContext.getFileTypes();
        const fileMimeTypes = await apiContext.getFileMimeTypes();
        const allFileTypes = [
          ...fileTypes.map((ft) => {
            return {
              ...ft,
              id: `fileType_${ft.id}`,
              type: 'fileType',
            };
          }),
          ...fileMimeTypes.map((ft) => {
            return {
              ...ft,
              id: `fileMImeType_${ft.id}`,
              type: 'fileMimeType',
            };
          }),
        ];
        setFileTypes(allFileTypes);
        setFileTypeOptions(
          allFileTypes.map((ft) => {
            return { value: ft.id, label: ft.name };
          }),
        );
      } catch (error: any) {
        setApiError(error);
      }
      setLoading(false);
    };

    getFileTypes();
  }, []);

  const onSystemPromptChange = (prompt: string) => {
    setSystemPrompt(prompt);
    const prompts = ftPrompts || [];
    const fileTypePrompt = prompts.find(
      (ftp: any) => ftp.fileType === fileType.name || ftp.fileMimeType === fileType.name,
    );
    if (fileTypePrompt) {
      fileTypePrompt.systemPrompt = prompt;
      fileTypePrompt.userPrompt = userPrompt;
    } else {
      prompts.push({
        fileType: fileType.type === 'fileType' ? fileType.name : undefined,
        fileMimeType: fileType.type === 'fileMimeType' ? fileType.name : undefined,
        systemPrompt: prompt,
        userPrompt,
      });
    }
    onChange(prompts);
  };

  const onUserPromptChange = (prompt: string) => {
    setUserPrompt(prompt);
    const prompts = ftPrompts || [];
    const fileTypePrompt = prompts.find(
      (ftp: any) => ftp.fileType === fileType.name || ftp.fileMimeType === fileType.name,
    );
    if (fileTypePrompt) {
      fileTypePrompt.systemPrompt = systemPrompt;
      fileTypePrompt.userPrompt = prompt;
    } else {
      prompts.push({
        fileType: fileType.type === 'fileType' ? fileType.name : undefined,
        fileMimeType: fileType.type === 'fileMimeType' ? fileType.name : undefined,
        systemPrompt,
        userPrompt: prompt,
      });
    }
    onChange(prompts);
  };

  const onFileTypeChange = (fileTypeId: string) => {
    const ft = fileTypes.find((ft) => fileTypeId === ft.id);
    if (ft) {
      const prompts = ftPrompts || [];
      const fileTypePrompt = prompts.find(
        (ftp: any) =>
          ftp.fileType?.toLowerCase() === ft.name?.toLowerCase() ||
          ftp.fileMimeType?.toLowerCase() === ft.name?.toLowerCase(),
      );
      if (fileTypePrompt) {
        setSystemPrompt(fileTypePrompt.systemPrompt);
        setUserPrompt(fileTypePrompt.userPrompt);
      } else {
        setSystemPrompt('');
        setUserPrompt('');
      }
    } else {
      setSystemPrompt('');
      setUserPrompt('');
    }
    setFileType(ft);
  };

  return (
    <Grid container spacing={1}>
      {label && (
        <Grid size={12}>
          <FormLabel required={required}>{label}</FormLabel>
        </Grid>
      )}
      <Grid size={6}>
        <FormLabel required={required}>{'File type'}</FormLabel>
      </Grid>
      <Grid size={6}>
        <SelectField options={fileTypeOptions} value={fileType?.id} required onChange={onFileTypeChange} />
      </Grid>
      {fileType && (
        <>
          <Grid size={12}>
            <TextField
              label={'System prompt'}
              value={systemPrompt}
              multiline
              rows={rows}
              onChange={onSystemPromptChange}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              label={'User prompt'}
              value={userPrompt}
              multiline
              rows={rows}
              required
              onChange={onUserPromptChange}
            />
          </Grid>
        </>
      )}
      {error && (
        <Grid size={12}>
          <FormHelperText error>{error}</FormHelperText>
        </Grid>
      )}
    </Grid>
  );
};

export default FileTypePrompts;
